import useSWR from "swr";
import { useMemo } from "react";
import { useGraphql } from "~/api-client";
import {
  DayOfTheWeek,
  FulfillmentType,
  Period,
} from "~/graphql-client/generated";
import { OrderDispatchOption, Product, TimeSlot } from "~/types";

export function useMenu(
  locationRef: string,
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
) {
  return _useMenu(locationRef, fulfillmentType, timeSlot, false);
}

export function useBaseMenu(
  locationRef: string,
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
) {
  return _useMenu(locationRef, fulfillmentType, timeSlot, true);
}

export function useCartMenu(
  locationRef: string,
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
  productIds: string[],
) {
  return _useMenu(locationRef, fulfillmentType, timeSlot, false, productIds);
}

function _useMenu(
  locationRef: string,
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
  baseMenuOnly: boolean,
  productIds?: string[],
) {
  const { getMenus, getBaseMenus } = useGraphql();

  const { data, isValidating, isLoading } = useSWR(
    [
      baseMenuOnly ? "getBaseMenus" : "getMenus",
      locationRef,
      fulfillmentType,
      productIds,
    ],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_name, arg1, arg2]) => {
      const enumType = toEnumFulfillmentType(arg2 || undefined);
      if (!enumType) return null;
      return baseMenuOnly
        ? getBaseMenus(arg1, enumType)
        : getMenus(arg1, enumType, productIds);
    },
    { revalidateOnFocus: false },
  );

  return useMemo(() => {
    const allMenus = data?.ordering?.menus ?? [];

    let menus = allMenus.filter(availableToTimeSlot(timeSlot));

    // If no menus are available and a time slot not selected, show the next
    // available menus
    if (!timeSlot && !menus.length) {
      menus = allMenus.reduce<[typeof allMenus, number]>(
        ([menus, bestTime], menu) => {
          const nextTime = nextAvailableTime(menu.serviceAvailability);
          if (!nextTime || nextTime > bestTime) return [menus, bestTime];
          if (nextTime === bestTime) return [menus.concat(menu), bestTime];
          return [[menu], nextTime];
        },
        [[], Number.MAX_SAFE_INTEGER],
      )[0];
    }

    const categories = menus.flatMap((m) => m.categories ?? []);

    const seen = new Set<string>();
    const menu = categories.filter((c) => {
      if (!c.products?.length) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });

    if (!fulfillmentType) {
      return {
        menu: [],
        isLoading: true,
        isValidating: true,
        serviceAvailability: null,
      };
    }

    return {
      menu,
      isLoading,
      isValidating,
      serviceAvailability: data?.ordering?.menus?.length
        ? data?.ordering?.menus[0].serviceAvailability
        : null,
    };
  }, [
    data?.ordering?.menus,
    fulfillmentType,
    isLoading,
    isValidating,
    timeSlot,
  ]);
}

export function useProduct(
  locationRef: string,
  productId?: string,
): { product: Product | null; loading: boolean } {
  const { getProduct } = useGraphql();

  const { data: details, isLoading: loading } = useSWR(
    productId ? ["productDetails", locationRef, productId] : null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_name, arg1, arg2]) => getProduct(arg1, arg2),
  );

  const product = details?.ordering?.product || null;

  if (!product?.available) return { product: null, loading };

  return {
    product,
    loading,
  };
}

//function copied from ordering-menu.ts
function toEnumFulfillmentType(
  option: OrderDispatchOption | undefined,
): FulfillmentType | undefined {
  if (option === "PICK_UP") {
    return FulfillmentType["PickUp"];
  }
  if (option === "DELIVERY") {
    return FulfillmentType["Delivery"];
  }
  if (option === "SERVICE") {
    return FulfillmentType["Service"];
  }
}

const DAYS = [
  DayOfTheWeek.Sunday,
  DayOfTheWeek.Monday,
  DayOfTheWeek.Tuesday,
  DayOfTheWeek.Wednesday,
  DayOfTheWeek.Thursday,
  DayOfTheWeek.Friday,
  DayOfTheWeek.Saturday,
];

function availableToTimeSlot(timeSlot: TimeSlot | null) {
  const d = timeSlot ? new Date(timeSlot.startTime) : new Date();

  const day = DAYS[d.getDay()];
  const time = d.toTimeString().slice(0, 5);

  return (m: { serviceAvailability?: Period[] | null }): boolean =>
    m.serviceAvailability?.some((p) => {
      // BUG: Currently assumes the user device is in the same timezone as the venue
      // https://loke.atlassian.net/browse/ORD-1582
      return p.dayOfTheWeek === day && p.start <= time && p.end > time;
    }) ?? false;
}

function nextAvailableTime(
  serviceAvailability?: Period[] | null,
): number | null {
  if (!serviceAvailability) return null;
  const now = new Date();

  return serviceAvailability?.reduce((best: number | null, period: Period) => {
    const start = new Date(now);

    const [hours, minutes] = period.start.split(":").map(Number);
    const day = DAYS.indexOf(period.dayOfTheWeek);

    start.setHours(hours, minutes, 0, 0);
    start.setDate(start.getDate() + ((day + 7 - start.getDay()) % 7));
    if (start < now) start.setDate(start.getDate() + 7);
    const startTime = start.getTime();

    if (!best) return startTime;
    if (startTime < best) return startTime;
    return best;
  }, null);
}
